import React from "react"
import { ArrowLeft } from "lucide-react"

const InterviewDetailsShimmer = ({ onBack }) => {
  return (
    <div
      className="h-screen flex flex-col overflow-hidden bg-zinc-50"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .hide-scroll::-webkit-scrollbar { display: none; }
        .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes shimmer {
          0% { background-position: -600px 0; }
          100% { background-position: 600px 0; }
        }
        .sh {
          background: linear-gradient(90deg, #f4f4f5 25%, #e4e4e7 50%, #f4f4f5 75%);
          background-size: 600px 100%;
          animation: shimmer 1.6s infinite linear;
          border-radius: 0;
        }
      `}</style>

      {/* ── Navbar ── */}
      <div className="bg-white border-b border-zinc-200 shrink-0 z-20">
        <div className="px-6 py-3 flex items-center justify-between">
          {/* Left */}
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center text-zinc-500 hover:text-zinc-800 transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="flex items-center gap-3">
              {/* Avatar placeholder */}
              <div className="w-7 h-7 sh" />
              <div className="flex flex-col gap-1.5">
                <div className="h-3.5 w-40 sh" />
                <div className="h-3 w-56 sh" />
              </div>
            </div>
          </div>

          {/* Right — action buttons */}
          <div className="flex items-center gap-2">
            <div className="h-8 w-16 sh" />
            <div className="h-8 w-20 sh" />
            <div className="h-8 w-24 sh" />
          </div>
        </div>

        {/* Page-level tabs */}
        <div className="flex px-6 border-t border-zinc-100">
          {["Interview Report", "Interview Recording"].map((label, i) => (
            <div key={label} className="flex items-center gap-2 px-4 py-3 mr-1">
              <div className="w-3 h-3 sh" />
              <div className="h-3 sh" style={{ width: i === 0 ? 108 : 128 }} />
            </div>
          ))}
        </div>
      </div>

      {/* ── Two-panel body ── */}
      <div className="flex flex-1 min-h-0 bg-zinc-100 gap-px">
        {/* ════ LEFT panel ════ */}
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto hide-scroll bg-zinc-50">
          {/* Candidate header card */}
          <div className="bg-zinc-50 px-10 py-5 shrink-0">
            <div className="bg-white border border-zinc-200 px-8 py-5 flex items-center justify-between gap-6">
              <div className="flex flex-col gap-3 min-w-0 flex-1">
                {/* Name + badges */}
                <div className="flex items-center gap-2.5 flex-wrap">
                  <div className="h-5 w-36 sh" />
                  <div className="h-5 w-44 sh" />
                  <div className="h-5 w-20 sh" />
                </div>
                {/* Meta row */}
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="h-3 w-32 sh" />
                  <div className="h-3 w-px bg-zinc-200" />
                  <div className="h-3 w-24 sh" />
                  <div className="h-3 w-px bg-zinc-200" />
                  <div className="h-3 w-20 sh" />
                </div>
              </div>
              {/* Score */}
              <div className="shrink-0 flex flex-col items-end gap-1.5 px-6 py-2">
                <div className="h-3 w-10 sh" />
                <div className="h-10 w-14 sh" />
                <div className="h-3 w-6 sh" />
              </div>
            </div>
          </div>

          {/* Stat strip — 4 columns */}
          <div className="shrink-0 bg-zinc-50 px-10 pb-5">
            <div className="grid grid-cols-4 bg-white border border-zinc-200">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`px-6 py-4 flex flex-col gap-2 ${i < 3 ? "border-r border-zinc-200" : ""}`}
                >
                  <div className="h-2.5 w-24 sh" />
                  <div className="h-5 w-16 sh" />
                  <div className="h-3 w-14 sh" />
                </div>
              ))}
            </div>
          </div>

          {/* Content sections */}
          <div className="px-10 py-7 space-y-4">
            {/* Interview Analysis card */}
            <div className="bg-white border border-zinc-200">
              <div className="px-6 py-3.5 border-b border-zinc-200">
                <div className="h-2.5 w-36 sh" />
              </div>
              <div className="px-6 py-5 grid grid-cols-3 gap-6 divide-x divide-zinc-100">
                {["Strengths", "Weaknesses", "Areas of Improvement"].map(
                  (col) => (
                    <div
                      key={col}
                      className={col !== "Strengths" ? "pl-6" : ""}
                    >
                      <div className="h-2.5 w-20 sh mb-3" />
                      <div className="space-y-3">
                        {[80, 64, 72, 56].map((w, j) => (
                          <div key={j} className="flex items-start gap-2.5">
                            <div className="w-1 h-1 shrink-0 mt-1.5 bg-zinc-200" />
                            <div
                              className="h-3 sh flex-1"
                              style={{ width: `${w}%` }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                )}
              </div>
              {/* Overall Assessment */}
              <div className="px-6 pb-5 pt-4 border-t border-zinc-100">
                <div className="h-2.5 w-36 sh mb-3" />
                <div className="space-y-2">
                  <div className="h-3 w-full sh" />
                  <div className="h-3 w-5/6 sh" />
                  <div className="h-3 w-4/6 sh" />
                </div>
              </div>
            </div>

            {/* Skills Analysis card */}
            <div className="bg-white border border-zinc-200">
              <div className="px-6 py-3.5 border-b border-zinc-200">
                <div className="h-2.5 w-28 sh" />
              </div>
              <div className="px-6 py-5 flex gap-8">
                {/* Radar placeholder */}
                <div style={{ width: "40%", minWidth: "40%", flexShrink: 0 }}>
                  <div className="h-[260px] w-full sh" />
                  <div className="mt-3 pt-3 border-t border-zinc-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="h-2.5 w-24 sh" />
                      <div className="h-3 w-12 sh" />
                    </div>
                    <div className="h-2 w-full sh" />
                  </div>
                </div>
                <div className="w-px bg-zinc-100 shrink-0" />
                {/* Skill breakdown */}
                <div className="flex-1 min-w-0">
                  <div className="h-2.5 w-28 sh mb-4" />
                  <div className="space-y-5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="h-3 w-24 sh" />
                          <div className="h-5 w-14 sh" />
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-1.5 sh" />
                          <div className="h-3 w-9 sh" />
                        </div>
                        <div className="h-3 w-4/5 sh mt-1" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Communication Skills card */}
            <div className="bg-white border border-zinc-200">
              <div className="px-6 py-3.5 border-b border-zinc-200 flex items-center justify-between">
                <div className="h-2.5 w-40 sh" />
                <div className="h-5 w-16 sh" />
              </div>
              <div className="px-6 py-5 flex gap-6">
                {/* Big score */}
                <div
                  className="shrink-0 flex flex-col items-center gap-3"
                  style={{ width: 130 }}
                >
                  <div className="h-12 w-12 sh" />
                  <div className="h-3 w-16 sh" />
                  <div className="h-7 w-full sh" />
                  <div className="h-3 w-24 sh" />
                  <div className="h-3 w-20 sh" />
                </div>
                <div className="w-px bg-zinc-100 shrink-0" />
                {/* Dimension grid */}
                <div className="flex-1 min-w-0">
                  <div className="h-2.5 w-32 sh mb-3" />
                  <div className="grid grid-cols-2 gap-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between px-3 py-2.5 border border-zinc-100 bg-zinc-50"
                      >
                        <div className="h-3 w-20 sh" />
                        <div className="h-3 w-6 sh" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ════ RIGHT panel (36%) ════ */}
        <div
          className="flex flex-col bg-white shrink-0"
          style={{ width: "36%" }}
        >
          {/* Interviewer Decision banner */}
          <div className="shrink-0 px-5 py-4 bg-white border-b border-zinc-200">
            <div className="border border-zinc-200 bg-zinc-50 px-5 py-4 flex items-center justify-between gap-4">
              <div className="flex flex-col gap-2.5">
                <div className="h-2.5 w-36 sh" />
                <div className="h-7 w-28 sh" />
              </div>
              <div className="h-9 w-32 sh shrink-0" />
            </div>
          </div>

          {/* Inner tabs */}
          <div className="flex border-b border-zinc-200 shrink-0 bg-white">
            <div className="flex-1 px-4 py-4 border-r border-zinc-200 flex justify-center">
              <div className="h-2.5 w-20 sh" />
            </div>
            <div className="flex-1 px-4 py-4 flex items-center justify-center gap-2">
              <div className="w-3 h-3 sh" />
              <div className="h-2.5 w-28 sh" />
            </div>
          </div>

          {/* Q-by-Q skeleton items */}
          <div className="flex-1 overflow-y-auto hide-scroll px-6 py-6 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="border border-zinc-200">
                {/* Question header */}
                <div className="px-4 py-3.5 bg-zinc-50 border-b border-zinc-200 flex items-start justify-between gap-3">
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="h-3 w-8 sh" />
                    <div className="h-3 w-full sh" />
                    <div className="h-3 w-3/4 sh" />
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1.5">
                    <div className="h-4 w-10 sh" />
                    <div className="h-5 w-14 sh" />
                  </div>
                </div>
                {/* Answer */}
                <div className="px-4 py-3.5 border-b border-zinc-100">
                  <div className="h-2.5 w-28 sh mb-2" />
                  <div className="bg-zinc-50 border border-zinc-100 px-3 py-2.5 space-y-1.5">
                    <div className="h-3 w-full sh" />
                    <div className="h-3 w-5/6 sh" />
                  </div>
                </div>
                {/* Feedback */}
                <div className="px-4 py-3.5">
                  <div className="h-2.5 w-16 sh mb-2" />
                  <div className="space-y-1.5">
                    <div className="h-3 w-full sh" />
                    <div className="h-3 w-4/5 sh" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default InterviewDetailsShimmer
